import { Close } from '@mui/icons-material';
import { Box, Button, IconButton, Modal, Stack } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import AuthSelect from '../../../components/AuthController/AuthSelect';
import AuthDatePicker from '../../../components/AuthController/AuthDatePicker';
import AuthInput from '../../../components/AuthController/AuthInput';
import { useDispatch, useSelector } from 'react-redux';
import AuthLocation from '../../../components/AuthController/AuthLocation';
import { genderFullOptions, genderOptions, imdbRegex, instagramRegex } from '../../../../constants';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMessage } from '../../../../base/MessageProvider';
import { matchIsValidTel } from 'mui-tel-input';
import { Bars } from 'react-loader-spinner';
import clsx from 'clsx';
import { updateSignupProfile } from '../../../actions/auth';
import { StorageConstants } from '../../../storage/storageConstants';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    width: '90%',
    bgcolor: 'white',
    paddingX: 5,
    paddingY: 3,
    borderRadius: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center' 
}
  
const validationSchema = Yup.object().shape({
      category: Yup.string().required('Profile category is required'),
      gender: Yup.string().required('Gender is required'),
      dob: Yup.string().required('Date of Birth is required'),
      email: Yup.string().trim().email("Invalid email id").required('Email id is required'),
      instagramHandle: Yup.string()
        .test("Valid Id", "Invalid username", (value) => !value || instagramRegex.test(value)),
      imdbLink: Yup.string()
        .test("Valid URL", "Invalid link", (value) => !value || imdbRegex.test(value)),
      location: Yup.string().required('Location is required'),
});

const BasicInfoModal = ({ open, onClose }) => {
    const navigate = useNavigate();
    const alert = useMessage();
    const dispatch = useDispatch();

    const locationList = useSelector(state => state.recruiterGeneralReducer?.locationList) ?? [];
    const categoryList = useSelector(state => state.recruiterGeneralReducer?.profileCategoryList) ?? [];
    const loading = useSelector((state) => state.recruiterAuthReducer?.loading);
    const mobileNo = useSelector((state) => state.recruiterAuthReducer?.mobileNo);

    const categoryOptions = useMemo(() => categoryList.map((item) => ({
        label: item.name, value: item.id
    })), [categoryList]);

    const {
        control, clearErrors, setError,
        formState: { errors }, reset, handleSubmit
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            category: '',
            gender: '',
            dob: null,
            email: '',
            location: '',
            imdbLink: '',
            instagramHandle: ''
        },
    });

    useEffect(() => {
        const checkAuthentications = async () => {
            const signupToken = localStorage.getItem(StorageConstants.SIGNUP_TOKEN);
            if (!signupToken) {
                navigate('/recruiter/auth/join', { replace: true });
                return;
            }
            clearErrors();
            reset();
        }
        checkAuthentications(); 
    }, []);

    const onSubmit = async (data) => {
        clearErrors();
        dispatch(updateSignupProfile({ ...data, mobileNo }, (res) => {
            alert.showSuccess(res?.message);
            navigate('/recruiter/auth/profile-pic', { replace: true });
        }, (error) => {
            alert.showError(error?.message ?? "Something went wrong!!");
        }))
    }


    return (
        <Modal disableAutoFocus sx={{ bgcolor: '#F4F4F499'}} hideBackdrop open={open}>
            <Box className="shadow-[0_0_14px_grey]" sx={boxStyle}>
                <IconButton onClick={onClose} className='absolute top-3 right-3' size='small'> 
                    <Close className='text-xl' />
                </IconButton>
                <Box className="mb-6">
                    <h2 className="font-['Poppins-Medium'] font-[500] text-[16px]">BASIC INFO</h2>
                </Box>
                <Stack className="w-full" spacing={2} my={1}>
                    <AuthSelect
                        placeholder='Select Category*' 
                        hookFormControl={control} 
                        varName="category" 
                        options={categoryOptions} size='small'
                        errorMessage={errors?.category?.message}
                    />
                    <AuthSelect
                        placeholder='Select Gender*' 
                        hookFormControl={control} 
                        varName="gender" 
                        options={genderFullOptions} size='small'
                        errorMessage={errors?.gender?.message}
                    />
                    <AuthDatePicker
                        placeholder='Select D.O.B*' 
                        hookFormControl={control} 
                        varName="dob" 
                        size='small'
                        maxDate={new Date()}
                        errorMessage={errors?.dob?.message}
                    />
                    <AuthInput
                        placeholder='Enter Email*' 
                        hookFormControl={control} 
                        varName="email" 
                        size='small'
                        errorMessage={errors?.email?.message}
                    />
                    <AuthLocation 
                        placeholder='Enter Location*' 
                        hookFormControl={control} 
                        varName="location" 
                        size='small'
                        errorMessage={errors?.location?.message}
                        options={locationList} 
                    />
                    <AuthInput
                        placeholder='Enter Instagram User Name' 
                        hookFormControl={control} 
                        varName="instagramHandle" 
                        size='small'
                        errorMessage={errors?.instagramHandle?.message}
                    />
                    <AuthInput
                        placeholder='Enter IMDb Link' 
                        hookFormControl={control} 
                        varName="imdbLink" 
                        size='small'
                        errorMessage={errors?.imdbLink?.message}
                    />
                </Stack>
                <Button onClick={handleSubmit(onSubmit)} disabled={loading} className={clsx("bg-[#2F80ED] flex gap-3 justify-center text-[#FFF] w-full rounded-full my-2 font-['Poppins-Medium'] font-[500]", { "bg-opacity-70": loading })}>
                    Submit
                    <Bars
                        height="20"
                        width={'20'}
                        color="#FFFFFF"
                        ariaLabel="bars-loading"
                        visible={loading}
                    />
                </Button>
            </Box>
        </Modal>
    );
}

export default React.memo(BasicInfoModal, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open
});